'use client';

import clsx from 'clsx';
import type { CSSProperties, ForwardedRef, ReactNode } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { StringLikeChildren } from '../types.js';
import type { CommonProps } from '../types.js';

export interface BadgeProps extends CommonProps {
  /** The content to display as the label. */
  children: StringLikeChildren;
  /**
   * Determines the color tone of the badge.
   * @default 'base'
   */
  tone?:
    | 'base'
    | 'success'
    | 'info'
    | 'warning'
    | 'critical'
    | 'lime'
    | 'emerald'
    | 'indigo'
    | 'purple'
    | 'pink'
    | 'brown';
  /**
   * The size of the badge.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * The icon to display at the start of the badge.
   *
   * Should not be used when size is set to `'small'` as icons won't be displayed at this size.
   */
  iconStart?: ReactNode;
}

function Badge(
  { children, iconStart, UNSAFE_className, UNSAFE_style, tone = 'base', size = 'medium', ...props }: BadgeProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <div
      {...props}
      ref={ref}
      className={clsx('cim-badge', UNSAFE_className)}
      style={
        {
          ...UNSAFE_style,
          '--cim-badge-color': `var(--cim-fg-${tone})`,
          '--cim-badge-background-color': `var(--cim-bg-${tone === 'base' ? 'subtle' : `${tone}-subtle`})`,
        } as CSSProperties
      }
      data-size={size}
    >
      {iconStart && <div className="cim-badge-icon">{iconStart}</div>}
      <span className="cim-badge-text">{children}</span>
    </div>
  );
}

/**
 * Displays a small piece of information, such as a status or a category.
 *
 * See [badge usage guidelines](https://ui.cimpress.io/components/badge/).
 */
const _Badge = withStyleProps(forwardRef(Badge), 'Badge');

export { _Badge as Badge };
